/**
 * AuthCallback — Handles the redirect-back from Emergent's OAuth flow.
 *
 * Flow:
 *   1. User clicked "Sign in with Google" → redirected to
 *      https://auth.emergentagent.com/?redirect=<our-origin>/dashboard
 *   2. Google → Emergent → us, with URL fragment #session_id=<id>
 *   3. This component reads session_id, POSTs to /api/auth/session, and
 *      then redirects to "/" (or the ?next= query) with the cookie set.
 *
 * The hash-detect happens at the App-router level (App.js) BEFORE Routes
 * runs, so this component only ever mounts when there IS a session_id.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import ElpmLogo from "@/components/ElpmLogo";

const API = process.env.REACT_APP_BACKEND_URL;

function _parseSessionIdFromHash(hash) {
  // hash typically looks like "#session_id=ABC123" — could also have extra
  // params after & if Emergent ever adds them. Be defensive.
  if (!hash) return null;
  const m = hash.match(/session_id=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function _readNextParam(search) {
  try {
    const u = new URLSearchParams(search);
    const n = u.get("next");
    if (n && n.startsWith("/") && !n.startsWith("//")) return n;
  } catch (e) { void e; }
  return "/";
}

export default function AuthCallback() {
  // useRef + synchronous flag — protects against StrictMode double-mount
  // firing the exchange twice. useState would lose the guard on the second
  // synchronous render under strict mode.
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const sessionId = _parseSessionIdFromHash(window.location.hash);
    const next = _readNextParam(window.location.search);

    if (!sessionId) {
      setStatus("missing-session");
      setTimeout(() => { window.location.replace("/"); }, 1500);
      return;
    }

    let anonToken = "";
    try { anonToken = window.localStorage.getItem("elpm-user-token") || ""; } catch (e) { void e; }

    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/session`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(anonToken ? { "X-Anon-Token": anonToken } : {}),
          },
          body: JSON.stringify({ session_id: sessionId }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }
        const body = await res.json();
        // Switch the user_token in localStorage to the authenticated user_id
        // (so post-auth API calls scope correctly + match the migrated rows).
        try {
          if (body?.user?.user_id) {
            window.localStorage.setItem("elpm-user-token", body.user.user_id);
          }
        } catch (e) { void e; }

        // Surface migration toast if anything was reassigned.
        try {
          const migrated = body?.migrated || {};
          const summary = Object.entries(migrated)
            .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
            .join(" · ");
          if (summary) {
            toast.success("Account synced", { description: `Brought across ${summary} from this browser.` });
          }
        } catch (e) { void e; }

        setStatus("done");
        // Clean the URL (remove the hash) and route forward.
        try {
          window.history.replaceState({}, "", next);
        } catch (e) { void e; }
        // Hard navigate so all hooks (useAuth, etc.) re-mount fresh.
        window.location.replace(next);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[auth] session exchange failed:", e);
        toast.error("Sign-in failed", { description: "Could not complete the Google sign-in. Please try again." });
        setStatus("failed");
        setTimeout(() => { window.location.replace("/"); }, 1800);
      }
    })();
  }, []);

  return (
    <div
      data-testid="auth-callback"
      className="flex min-h-screen items-center justify-center bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 text-white"
    >
      <div className="flex flex-col items-center gap-4">
        <ElpmLogo className="mb-2 h-12" testId="auth-callback-logo" />
        {/* Pulse ring */}
        <span className="relative grid h-12 w-12 place-items-center">
          <span className="absolute h-12 w-12 animate-ping rounded-full bg-fuchsia-500/45" />
          <span className="relative h-3 w-3 rounded-full bg-fuchsia-300" />
        </span>
        <p
          data-testid="auth-callback-status"
          className="font-mono text-[11px] uppercase tracking-[0.22em] text-fuchsia-200/85"
        >
          {status === "processing" && "Signing you in…"}
          {status === "done" && "Welcome — redirecting…"}
          {status === "failed" && "Sign-in failed — sending you back to the studio…"}
          {status === "missing-session" && "No session found — back to the studio…"}
        </p>
      </div>
    </div>
  );
}
